import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDeploy } from '../contexts/DeployContext';

function formatDate(dateString) {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${mo}-${da} ${h}:${mi}`;
  } catch {
    return dateString;
  }
}

function NotificationsTable({ selected, onSelect, refreshKey = 0, onChanged }) {
  const { deployApi } = useDeploy();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await deployApi.notifications.list();
      setRows(res?.notifications ?? []);
    } catch (err) {
      setError(err?.message ?? 'Failed to load notifications');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [deployApi]);

  useEffect(() => { fetchRows(); }, [fetchRows, refreshKey]);

  const handleToggleStatus = useCallback(async (row, e) => {
    e.stopPropagation();
    try {
      if (row.status === 'active') {
        await deployApi.notifications.pause(row.notification_id);
      } else {
        await deployApi.notifications.activate(row.notification_id);
      }
      await fetchRows();
      onChanged?.();
    } catch (err) {
      setError(err?.message ?? 'Status change failed');
    }
  }, [deployApi, fetchRows, onChanged]);

  const handleDelete = useCallback(async (row, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete notification "${row.name}"? This cannot be undone.`)) return;
    try {
      await deployApi.notifications.delete(row.notification_id);
      await fetchRows();
      onChanged?.();
    } catch (err) {
      setError(err?.message ?? 'Delete failed');
    }
  }, [deployApi, fetchRows, onChanged]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Typography color="error" variant="body2">{error}</Typography>
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No notifications yet. Create one in the right column.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', border: 'none' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Algos</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Daily budget</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((n) => (
            <TableRow
              key={n.notification_id}
              hover
              onClick={() => onSelect(n)}
              selected={selected?.notification_id === n.notification_id}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>{n.notification_id}</TableCell>
              <TableCell>{n.name}</TableCell>
              <TableCell>{Array.isArray(n.algo_ids) ? n.algo_ids.length : 0}</TableCell>
              <TableCell>
                <Chip
                  label={n.status}
                  size="small"
                  color={n.status === 'active' ? 'success' : 'default'}
                  variant={n.status === 'active' ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell>{n.guards?.daily_budget ?? '—'}</TableCell>
              <TableCell>{formatDate(n.updated_at)}</TableCell>
              <TableCell align="right">
                <Tooltip title={n.status === 'active' ? 'Pause' : 'Activate'}>
                  <IconButton size="small" onClick={(e) => handleToggleStatus(n, e)}>
                    {n.status === 'active' ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={(e) => handleDelete(n, e)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default NotificationsTable;
